import { useMemo, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import { type TrialDto, type TrialListQuery } from '@ctms/contracts';
import { Search } from 'lucide-react';

import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { CreateTrialDialog } from '../create-trial-dialog/create-trial-dialog.js';
import { SponsorCroPanels } from '../dictionary-panels.js';
import { trialsApi } from '../trials.api.js';
import { DICTIONARY_LIST_QUERY, trialQueryKeys } from '../trials.query-keys.js';

export interface TrialCatalogProps {
  canRead: boolean;
  canWrite: boolean;
  isTenantAdmin: boolean;
  onOpenTrial: (trial: TrialDto) => void;
}

const columnHelper = createAppColumnHelper<TrialDto>();

const LIST_LIMIT = 25;

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

export function TrialCatalog({
  canRead,
  canWrite,
  isTenantAdmin,
  onOpenTrial,
}: TrialCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const listFilters: TrialListQuery = useMemo(() => {
    const filters: TrialListQuery = { page, limit: LIST_LIMIT };
    if (!showInactive) {
      filters.active = true;
    }
    const trimmed = search.trim();
    if (trimmed.length > 0) {
      filters.search = trimmed;
    }
    return filters;
  }, [page, search, showInactive]);

  const trialsQuery = useQuery({
    queryKey: trialQueryKeys.list(listFilters),
    queryFn: () => trialsApi.listTrials(listFilters),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const sponsorsQuery = useQuery({
    queryKey: trialQueryKeys.sponsors.list(DICTIONARY_LIST_QUERY),
    queryFn: () => trialsApi.listSponsors(DICTIONARY_LIST_QUERY),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const crosQuery = useQuery({
    queryKey: trialQueryKeys.cros.list(DICTIONARY_LIST_QUERY),
    queryFn: () => trialsApi.listCros(DICTIONARY_LIST_QUERY),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const sponsors = sponsorsQuery.data?.items ?? [];
  const cros = crosQuery.data?.items ?? [];
  const sponsorName = useMemo(() => new Map(sponsors.map((sponsor) => [sponsor.id, sponsor.name])), [sponsors]);
  const croName = useMemo(() => new Map(cros.map((cro) => [cro.id, cro.name])), [cros]);

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('shortcut', { header: 'Skrót' }),
        columnHelper.accessor('shortTitle', { header: 'Tytuł' }),
        columnHelper.accessor('protocolNumber', { header: 'Protokół' }),
        columnHelper.display({
          id: 'sponsor',
          header: 'Sponsor',
          cell: (info) => sponsorName.get(info.row.original.sponsorId) ?? '—',
        }),
        columnHelper.display({
          id: 'cro',
          header: 'CRO',
          cell: (info) => {
            const name = info.row.original.croId == null ? undefined : croName.get(info.row.original.croId);
            if (!name) {
              return (
                <span>
                  — <span className="sr-only">Brak organizacji prowadzącej</span>
                </span>
              );
            }
            return name;
          },
        }),
        columnHelper.accessor('siteNumber', { header: 'Numer ośrodka' }),
        columnHelper.accessor('active', {
          header: 'Stan',
          cell: (info) => (info.getValue() ? 'Aktywne' : 'Nieaktywne'),
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Działania',
          cell: (info) => (
            <button
              type="button"
              aria-label={`Otwórz ${info.row.original.shortcut}`}
              onClick={() => onOpenTrial(info.row.original)}
              className="rounded border border-cyan-800/60 px-2 py-1 text-xs text-cyan-300"
            >
              Otwórz
            </button>
          ),
        }),
      ]),
    [croName, onOpenTrial, sponsorName],
  );

  const items = trialsQuery.data?.items ?? [];
  const table = useAppTable({ data: items, columns });

  if (!canRead) {
    return (
      <section role="alert" className="rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h2 className="text-base font-semibold text-slate-100">Brak dostępu do badań</h2>
        <p className="mt-2 text-xs text-slate-400">
          Nie masz uprawnień do przeglądania katalogu badań. Jeżeli uważasz, że to błąd, skontaktuj się z administratorem ośrodka.
        </p>
      </section>
    );
  }

  const isLoading = trialsQuery.isLoading || sponsorsQuery.isLoading || crosQuery.isLoading;
  const isError = trialsQuery.isError || sponsorsQuery.isError || crosQuery.isError;
  const isEmptyWorkingList = !isLoading && !isError && items.length === 0 && !showInactive && search.trim().length === 0;

  const retry = (): void => {
    void trialsQuery.refetch();
    void sponsorsQuery.refetch();
    void crosQuery.refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-900/80 p-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute top-2 left-2.5 h-3.5 w-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            aria-label="Szukaj po skrócie lub tytule"
            placeholder="Szukaj po skrócie lub tytule…"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pr-3 pl-8 text-xs text-slate-100"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => {
              setShowInactive(event.target.checked);
              setPage(1);
            }}
          />
          Pokaż nieaktywne
        </label>
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
        >
          Nowe badanie
        </button>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-400">Ładowanie katalogu</p>
      ) : isError ? (
        <section role="alert" className="rounded-lg border border-rose-800/70 bg-rose-950/40 p-6">
          <h2 className="text-sm font-semibold text-rose-200">Nie udało się wczytać katalogu</h2>
          <button type="button" onClick={retry} className="mt-3 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200">
            Spróbuj ponownie
          </button>
        </section>
      ) : isEmptyWorkingList ? (
        <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-8 text-center">
          <h2 className="text-base font-semibold text-slate-100">Brak badań aktywnych</h2>
          <p className="mt-2 text-xs text-slate-400">W ośrodku nie ma jeszcze aktywnego badania na liście roboczej.</p>
        </section>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-slate-500">
                    Brak badań spełniających kryteria wyszukiwania.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-800/80">
                    {row.getAllCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2">
                        <table.FlexRender cell={cell} />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !isError && (trialsQuery.data?.total ?? 0) > LIST_LIMIT && (
        <div className="flex justify-end gap-2 text-xs">
          <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            Poprzednia
          </button>
          <button
            type="button"
            disabled={!trialsQuery.data?.hasNext}
            onClick={() => setPage((current) => current + 1)}
          >
            Następna
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <SponsorCroPanels sponsors={sponsors} cros={cros} isTenantAdmin={isTenantAdmin} />
      )}

      <CreateTrialDialog
        isOpen={isCreateOpen}
        canWrite={canWrite}
        isTenantAdmin={isTenantAdmin}
        sponsors={sponsors}
        cros={cros}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: trialQueryKeys.lists() });
        }}
      />
    </div>
  );
}
